import { AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"

export type VideoConsiderScaleCheckboxLabels = {
  title: string
  hintBefore: string
  hintBeforeCompact?: string
  hintLink: string
  hintAfter: string
  disabled?: string
}

export type VideoConsiderScaleCheckboxProps = {
  scaleApplicable: boolean
  scaleSheetLinkEnabled: boolean
  videoConsiderScale: boolean
  onVideoConsiderScaleChange: (value: boolean) => void
  onOpenScaleSheet: () => void
  /** compact (default): canvas one-line; detailed: shot title + hint After. */
  presentation?: "compact" | "detailed"
  className?: string
  /** Override default character-scale i18n strings (e.g. mixed scale sheet). */
  labels?: VideoConsiderScaleCheckboxLabels
  /** Shown under hint when some cast members lack scale-sheet figures. */
  warning?: string | null
}

/** Scale-sheet toggle + link (shot video extras / canvas video composer). */
export function VideoConsiderScaleCheckbox({
  scaleApplicable,
  scaleSheetLinkEnabled,
  videoConsiderScale,
  onVideoConsiderScaleChange,
  onOpenScaleSheet,
  presentation = "compact",
  className,
  labels,
  warning,
}: VideoConsiderScaleCheckboxProps) {
  const { t } = useTranslation(["shot"])

  const title = labels?.title ?? t("common.videoConsiderScale")
  const hintBefore = labels?.hintBefore ?? t("common.videoConsiderScaleHintBefore")
  const hintBeforeCompact = labels?.hintBeforeCompact ?? t("common.videoConsiderScaleHintBeforeCompact")
  const hintLink = labels?.hintLink ?? t("common.videoConsiderScaleHintLink")
  const hintAfter = labels?.hintAfter ?? t("common.videoConsiderScaleHintAfter")
  const disabled = labels?.disabled ?? t("common.videoConsiderScaleDisabled")

  const linkButton = (
    <button
      type="button"
      className={cn(
        "cursor-pointer text-ui-link underline-offset-2 hover:text-ui-accent-text hover:underline",
        presentation === "detailed" && "font-medium",
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onOpenScaleSheet()
      }}
    >
      {hintLink}
    </button>
  )

  const linkMuted = (
    <span
      className={cn("cursor-default text-ui-text-muted", presentation === "detailed" && "font-medium")}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {hintLink}
    </span>
  )

  const linkOrMuted = scaleSheetLinkEnabled ? linkButton : linkMuted

  if (presentation === "detailed") {
    return (
      <div
        className={cn(
          "rounded-lg border border-ui-border px-3 py-2.5 dark:bg-ui-bg-elevated/50 light:bg-clay-1",
          className,
        )}
      >
        <label
          className={cn(
            "flex items-start gap-2 text-xs text-ui-text-secondary",
            scaleApplicable ? "cursor-pointer" : "cursor-not-allowed opacity-70",
          )}
        >
          <input
            type="checkbox"
            className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken disabled:opacity-50"
            checked={scaleApplicable && videoConsiderScale}
            disabled={!scaleApplicable}
            onChange={(e) => onVideoConsiderScaleChange(e.target.checked)}
          />
          <span>
            <span className="font-medium text-ui-text-primary">{title}</span>
            <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
              {scaleApplicable ? (
                <>
                  {hintBefore}
                  {linkOrMuted}
                  {hintAfter}
                </>
              ) : (
                disabled
              )}
            </span>
            {warning ? (
              <span className="mt-1.5 flex items-start gap-1 text-[10px] leading-snug text-ui-status-rendering">
                <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
                <span>{warning}</span>
              </span>
            ) : null}
          </span>
        </label>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "w-full max-w-[18rem] min-w-0 rounded-lg border border-ui-border px-3 py-2 dark:bg-ui-bg-elevated/50 light:bg-clay-1",
        className,
      )}
    >
      <label
        className={cn(
          "flex items-start gap-2 text-xs text-ui-text-primary",
          scaleApplicable ? "cursor-pointer" : "cursor-not-allowed opacity-70",
        )}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken disabled:opacity-50"
          checked={scaleApplicable && videoConsiderScale}
          disabled={!scaleApplicable}
          onChange={(e) => onVideoConsiderScaleChange(e.target.checked)}
        />
        <span className="min-w-0 leading-snug font-medium text-ui-text-primary">
          {scaleApplicable ? (
            <>
              {hintBeforeCompact}
              {linkOrMuted}
            </>
          ) : (
            disabled
          )}
        </span>
      </label>
      {warning ? (
        <span className="mt-1.5 flex max-w-full items-start gap-1 text-[10px] leading-snug text-ui-status-rendering">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
          <span>{warning}</span>
        </span>
      ) : null}
    </div>
  )
}
